import {
  GetSustainabilityPageDocument,
  type GetSustainabilityPageQuery,
  type GetSustainabilityPageQueryVariables,
  type StrapiSustainabilityPage,
} from '~/apollo/types/types';

export default async () => {
  const { $sentry } = useNuxtApp();
  const { locale } = useI18n();
  const { redirectToDefaultLocale } = useRedirectToLocale();
  const indexFetched = ref(false);
  const {
    refresh: refreshSustainabilityPage,
    data: sustainabilityPageResponse,
  } = await useAsyncData('gp-sustainability-page', async () => {
    const ret = {
      sustainabilityPage: null as StrapiSustainabilityPage,
    };

    try {
      if (!indexFetched.value) {
        const { data: sustainabilityPageData, error: sustainabilityPageError } =
          await useLegacyApollo<
            GetSustainabilityPageQuery,
            GetSustainabilityPageQueryVariables
          >(GetSustainabilityPageDocument, {
            locale: locale.value,
          });

        if (sustainabilityPageData.sustainabilityPage == null) {
          return ret;
        }

        if (sustainabilityPageError != null) {
          $sentry.captureException(sustainabilityPageError);
          return ret;
        }

        ret.sustainabilityPage =
          sustainabilityPageData.sustainabilityPage as StrapiSustainabilityPage;

        indexFetched.value = true;
      }
    } catch (error) {
      $sentry.captureException(error);
    }

    return ret;
  });

  useI18nLocalizedSlugs(
    sustainabilityPageResponse.value?.sustainabilityPage?.localizedSlugs,
  );

  if (
    sustainabilityPageResponse.value.sustainabilityPage == null &&
    locale.value !== 'en'
  ) {
    await redirectToDefaultLocale();
  }
  const sustainabilityPage = computed(
    () => sustainabilityPageResponse.value?.sustainabilityPage,
  );

  useHead({
    title: () => sustainabilityPage.value?.metaTitle,
  });

  const config = useRuntimeConfig();
  const route = useRoute();
  useSeoMeta({
    title: () => sustainabilityPage.value?.metaTitle,
    description: () => sustainabilityPage.value?.metaDescription,
    ogImage: () => sustainabilityPage.value?.headerCoverImage.url,
    ogTitle: () => sustainabilityPage.value?.metaTitle,
    ogDescription: () => sustainabilityPage.value?.metaDescription,
    ogImageAlt: () =>
      sustainabilityPage.value?.headerCoverImage.alternativeText,
    ogUrl: () => `${config.public.websiteUrl}${route.fullPath}`,
    twitterCard: 'summary_large_image',
    twitterImage: () => sustainabilityPage.value?.headerCoverImage.url,
    twitterTitle: () => sustainabilityPage.value?.metaTitle,
    twitterDescription: () => sustainabilityPage.value?.metaDescription,
    twitterImageAlt: () =>
      sustainabilityPage.value?.headerCoverImage.alternativeText,
  });

  return {
    sustainabilityPage,
    refreshSustainabilityPage,
  };
};
